(function () {
  'use strict';

  const STEPS = ['Nennweite', 'Höhe', 'Wandabstand', 'Zusammenfassung'];
  const CUSTOMER_PART_LABELS = Object.freeze({
    WALL_SUPPORT: 'Wandstütze mit Querträger',
    BASE_PLATE: 'Grundplatte mit Kondensatablauf',
    INSPECTION: 'Revisionselement',
    TEE_90: 'T-Anschluss 90°',
    WALL_PASS: 'Kürzbares Anschlusselement mit integriertem Wandfutter',
    LENGTH_1000: 'Längenelement 1.000 mm',
    LENGTH_500: 'Längenelement 500 mm',
    LENGTH_250: 'Längenelement 250 mm',
    TERMINAL: 'Mündungsabschluss',
    WALL_HOLDER: 'Wandhalter'
  });

  function parseJson(element) {
    try {
      return JSON.parse(element?.textContent || '{}');
    } catch (error) {
      console.error('DW-ECO 2.0 Konfigurator: JSON konnte nicht gelesen werden.', error);
      return {};
    }
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  class JeremiasDwEcoConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rules = window.JeremiasDwEcoRules;
      this.pricing = window.JeremiasDwEcoPreviewPricing;
      this.kitVariants = window.JeremiasDwEcoKitVariants;
      this.settings = parseJson(this.querySelector('[data-dweco-settings]'));
      this.catalog = parseJson(this.querySelector('[data-dweco-catalog]'));
      this.app = this.querySelector('[data-dweco-app]');

      if (!this.rules || !this.pricing || !this.kitVariants || !this.app) {
        if (this.app) this.app.innerHTML = '<p class="chimney-configurator__error" role="alert">Der Konfigurator konnte nicht geladen werden.</p>';
        return;
      }

      this.selection = Object.assign(this.rules.defaultState(), {
        dn: Number(this.settings.initialDn || 130),
        heightClassId: String(this.settings.initialHeightClassId || 'H7'),
        wallDistanceMm: Number(this.settings.initialWallDistanceMm || 100)
      });
      this.previewOnly = this.settings.previewOnly !== false;
      this.step = 0;
      this.returnToSummary = false;
      this.partsOpen = false;
      this.submitting = false;
      this.cartMessage = '';
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('toggle', (event) => {
        if (!event.target.matches?.('.chimney-configurator__parts')) return;
        this.partsOpen = event.target.open;
      }, true);
      this.render();
    }

    resolve() {
      const result = this.rules.resolveConfiguration(this.selection, this.catalog);
      const previewPriceCents = this.pricing.getPriceCents(
        result.state.dn,
        result.state.heightClassId,
        result.state.wallDistanceMm
      );
      const kitVariant = this.kitVariants.get(
        result.state.dn,
        result.state.heightClassId,
        result.state.wallDistanceMm
      );
      const kitCartEligible = Boolean(
        result.cartEligible
        && kitVariant
        && /^\d+$/.test(String(kitVariant.variantId || ''))
        && Number(kitVariant.priceCents) === previewPriceCents
      );
      return Object.assign({}, result, { previewPriceCents, kitVariant, kitCartEligible });
    }

    money(cents) {
      if (!Number.isInteger(cents)) return 'Derzeit nicht verfügbar';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency', currency: this.settings.currency || 'EUR'
      }).format(cents / 100);
    }

    millimetres(value) {
      return `${new Intl.NumberFormat('de-DE').format(Number(value))} mm`;
    }

    render() {
      const result = this.resolve();
      this.result = result;
      const progress = Math.round(((this.step + 1) / STEPS.length) * 100);
      const price = this.money(result.previewPriceCents);

      this.app.innerHTML = `
        <div class="chimney-configurator__head">
          <div class="chimney-configurator__head-system">
            <strong class="chimney-configurator__config-title">Jeremias DW-ECO 2.0 Bausatz</strong>
            <span class="chimney-configurator__diameter">DN ${result.state.dn} · Wandmontage</span>
          </div>
          <div class="chimney-configurator__head-price">
            <strong class="chimney-configurator__live-price" role="status" aria-live="polite">${price}</strong>
            <small>Gesamtpreis inkl. MwSt.</small>
          </div>
        </div>
        <div class="chimney-configurator__progress" aria-label="Schritt ${this.step + 1} von ${STEPS.length}">
          <div class="chimney-configurator__progress-row">
            <strong class="chimney-configurator__progress-name">${STEPS[this.step]}</strong>
            <span class="chimney-configurator__progress-count">Schritt ${this.step + 1} von ${STEPS.length}</span>
          </div>
          <i class="chimney-configurator__progress-track" style="--progress:${progress}%"></i>
        </div>
        <div class="chimney-configurator__workspace">
          <aside class="chimney-configurator__preview" aria-live="polite">${this.preview(result)}</aside>
          <form class="chimney-configurator__panel" data-wizard-form novalidate>
            ${this.renderDiameterStep(result)}
            ${this.renderHeightStep(result)}
            ${this.renderDistanceStep(result)}
            ${this.renderSummary(result)}
            <div class="chimney-configurator__navigation">
              ${this.step > 0 ? '<button class="button button--secondary" type="button" data-back>Zurück</button>' : ''}
              ${this.step < STEPS.length - 1 ? `<button class="button" type="button" data-next>${this.returnToSummary ? 'Zur Zusammenfassung' : 'Weiter'}</button>` : ''}
            </div>
          </form>
        </div>`;
    }

    renderDiameterStep(result) {
      return `<section class="chimney-configurator__step" data-step="0"${this.step !== 0 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Welche Nennweite benötigen Sie?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die Nennweite passend zu Ihrer Feuerstätte und Schornsteinplanung.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--diameter">
          ${this.rules.PRIMARY_DNS.map((dn) => this.card({
            name: 'dn', value: dn, selected: result.state.dn === dn,
            title: `DN ${dn}`, hint: `Innen-Ø ${dn} mm`, kind: 'diameter'
          })).join('')}
        </div>
      </section>`;
    }

    renderHeightStep(result) {
      return `<section class="chimney-configurator__step" data-step="1"${this.step !== 1 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wie hoch soll Ihr Schornstein werden?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die gewünschte Höhenklasse. Die tatsächliche Aufbauhöhe wird passend zur gewählten Nennweite berechnet.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--height dweco-height-cards">
          ${this.rules.HEIGHT_RECIPES.map((recipe) => this.card({
            name: 'heightClassId', value: recipe.id, selected: result.state.heightClassId === recipe.id,
            title: recipe.customerLabel,
            hint: `Technische Höhe: ${this.millimetres(this.rules.calculateTechnicalHeightMm(result.state.dn, recipe.id))}`,
            kind: 'height'
          })).join('')}
        </div>
      </section>`;
    }

    renderDistanceStep(result) {
      return `<section class="chimney-configurator__step" data-step="2"${this.step !== 2 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Welchen Wandabstand benötigen Sie?</div>
        <p class="chimney-configurator__step-intro">Der Konfigurator ist für eine gerade Wandmontage an einer geeigneten Außenwand ausgelegt.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--distance">
          ${this.rules.WALL_DISTANCES_MM.map((distance) => this.card({
            name: 'wallDistanceMm', value: distance, selected: result.state.wallDistanceMm === distance,
            title: `${distance} mm`, kind: 'distance'
          })).join('')}
        </div>
        <p class="chimney-configurator__schematic-note dweco-mounting-note">Befestigungsuntergrund und Verankerung müssen für die vorhandene Gebäudesituation geeignet sein.</p>
      </section>`;
    }

    renderSummary(result) {
      if (this.step !== 3) return '<section class="chimney-configurator__step" data-step="3" hidden></section>';
      const total = this.money(result.previewPriceCents);
      return `<section class="chimney-configurator__step" data-step="3">
        <div class="chimney-configurator__step-title h3" tabindex="-1">Ihr Jeremias DW-ECO 2.0 Bausatz</div>
        <p class="chimney-configurator__step-intro">Prüfen Sie Ihre Auswahl und den zusammengestellten Bausatz.</p>
        <div class="chimney-configurator__compact-summary dweco-selection-summary">
          ${this.summaryChoice('Nennweite', `DN ${result.state.dn}`, 0)}
          ${this.summaryChoice('Höhe', `${result.height.customerLabel} · ${this.millimetres(result.height.technicalMm)}`, 1)}
          ${this.summaryChoice('Wandabstand', `${result.state.wallDistanceMm} mm`, 2)}
        </div>
        <p class="dweco-summary-system">Jeremias DW-ECO 2.0 · doppelwandig · gerade Außenwandführung mit Wandmontage</p>
        <p class="dweco-summary-scope"><strong>Standardkonfiguration:</strong> gerade Außenwandführung mit Wandmontage. Versätze, Dachdurchführungen und andere Sonderlösungen benötigen zusätzliche Planung.</p>
        <div class="chimney-configurator__summary-total">
          <small>Gesamtpreis</small><strong>${total}</strong><p>inklusive aller enthaltenen Komponenten</p>
        </div>
        <p class="dwfu-delivery"><span aria-hidden="true">✓</span><span><strong>Lieferzeit: ca. 1–2 Wochen</strong><small>Für den vollständigen Bausatz</small></span></p>
        <details class="chimney-configurator__parts"${this.partsOpen ? ' open' : ''}>
          <summary>Enthaltene Bauteile</summary>
          <ul class="dwfu-parts-list">${result.bom.map((line) => `<li><strong>${line.quantity} × ${escapeHtml(this.partLabel(line))}</strong><em>${this.money(Number.isInteger(line.unitPriceCents) ? line.unitPriceCents * line.quantity : null)}</em></li>`).join('')}</ul>
        </details>
        <div class="dwfu-advisory dweco-summary-notices">
          <p>Das kürzbare Anschlusselement wird bei der Montage an die tatsächliche Wandsituation angepasst.</p>
          <p>Befestigungsuntergrund und Verankerung müssen für die vorhandene Gebäudesituation geeignet sein.</p>
        </div>
        <button class="button button--full-width${this.previewOnly ? ' dweco-preview-cta' : ''}" type="button" data-add-configuration${this.previewOnly || !result.kitCartEligible || this.submitting ? ' disabled' : ''}>${this.submitting ? 'Wird hinzugefügt …' : 'Bausatz in den Warenkorb'}</button>
        <p class="chimney-configurator__cart-note">Alle Bestandteile werden gemeinsam als eine Konfiguration in den Warenkorb gelegt.</p>
        ${this.cartMessage ? `<div class="chimney-configurator__error" role="alert">${escapeHtml(this.cartMessage)}</div>` : ''}
      </section>`;
    }

    card({ name, value, selected, title, hint, kind }) {
      return `<label class="chimney-configurator__card chimney-configurator__card--compact chimney-configurator__card--${kind}${selected ? ' is-selected' : ''}">
        <input type="radio" name="${escapeHtml(name)}" data-selection="${escapeHtml(name)}" value="${escapeHtml(value)}"${selected ? ' checked' : ''}>
        <strong>${escapeHtml(title)}</strong>${hint ? `<small>${escapeHtml(hint)}</small>` : ''}
      </label>`;
    }

    summaryChoice(label, value, step) {
      return `<span><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong><button type="button" data-edit-step="${step}" aria-label="${escapeHtml(label)} ändern">Ändern</button></span>`;
    }

    partLabel(line) {
      if (line.role === 'WALL_SUPPORT') return `${CUSTOMER_PART_LABELS.WALL_SUPPORT} (${line.articleCode === 'DW391' ? 'Typ I' : line.articleCode === 'DW392' ? 'Typ II' : 'Typ III'})`;
      if (line.role === 'WALL_HOLDER') return `${CUSTOMER_PART_LABELS.WALL_HOLDER} für ${this.selection.wallDistanceMm} mm Wandabstand`;
      return CUSTOMER_PART_LABELS[line.role] || line.label || 'Systembauteil';
    }

    preview(result) {
      const totalHeight = result.height.technicalMm;
      const pipeBottom = 442;
      const supportStrokeWidth = 7;
      const supportBottomY = pipeBottom - supportStrokeWidth / 2;
      const pipeTop = Math.max(48, 215 - ((totalHeight - 1794) / (8430 - 1794)) * 150);
      const wallX = 176;
      const distance = Math.max(40, Math.min(88, 34 + result.state.wallDistanceMm / 7));
      const pipeWidth = 34;
      const pipeX = wallX - distance - pipeWidth;
      const pipeCenter = pipeX + pipeWidth / 2;
      const holderCount = Math.max(1, Math.min(5, result.holder.quantity || 1));
      const topHolderY = Math.min(368, pipeTop + (Math.min(2000, totalHeight) / totalHeight) * (pipeBottom - pipeTop));
      const holders = Array.from({ length: holderCount }, (_, index) => {
        const y = holderCount === 1 ? 368 : 368 - (index / (holderCount - 1)) * (368 - topHolderY);
        return `<g aria-hidden="true"><line x1="${pipeX + pipeWidth}" y1="${y}" x2="${wallX}" y2="${y}" stroke="#6f7474" stroke-width="4"/><rect x="${wallX - 3}" y="${y - 7}" width="6" height="14" class="dwfu-preview-bracket"/><circle cx="${pipeCenter}" cy="${y}" r="19" fill="none" stroke="#858a8a" stroke-width="2"/></g>`;
      }).join('');
      const segmentCount = Math.max(3, Math.min(9, Math.round(totalHeight / 1000)));
      const segments = Array.from({ length: segmentCount }, (_, index) => {
        const y = pipeBottom - ((pipeBottom - pipeTop) / segmentCount) * (index + 1);
        return `<line x1="${pipeX - 2}" y1="${y}" x2="${pipeX + pipeWidth + 2}" y2="${y}" stroke="#737778" stroke-width="2"/>`;
      }).join('');
      const support = `<path d="M ${pipeX + pipeWidth} 425 H ${wallX} V ${supportBottomY} H ${pipeX + pipeWidth}" class="chimney-configurator__preview-support" fill="none" stroke="#646969" stroke-width="${supportStrokeWidth}"/><rect x="${wallX - 5}" y="414" width="10" height="38" class="dwfu-preview-bracket"/>`;

      return `<div class="chimney-configurator__preview-stage">
        <p class="chimney-configurator__preview-label">Vorschau</p>
        <svg viewBox="0 0 360 520" role="img" aria-label="Schematische Vorschau eines Jeremias DW-ECO 2.0 Edelstahlschornsteins">
          <path d="M ${wallX} 94 H 356 V 468 H ${wallX} Z" class="dwfu-preview-building"/>
          <rect x="226" y="142" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="142" x2="262" y2="216" stroke="#c9c9c5" stroke-width="2"/><line x1="226" y1="179" x2="298" y2="179" stroke="#c9c9c5" stroke-width="2"/>
          <rect x="226" y="282" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="282" x2="262" y2="356" stroke="#c9c9c5" stroke-width="2"/><line x1="226" y1="319" x2="298" y2="319" stroke="#c9c9c5" stroke-width="2"/>
          ${holders}${support}
          <rect x="${pipeX}" y="${pipeTop}" width="${pipeWidth}" height="${pipeBottom - pipeTop}" class="dwfu-preview-steel"/>
          <rect x="${pipeX + 7}" y="${pipeTop + 3}" width="5" height="${pipeBottom - pipeTop - 6}" class="dwfu-preview-highlight"/>
          ${segments}
          <path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" fill="none" stroke="#707475" stroke-width="28"/><path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" fill="none" stroke="#d8d9d8" stroke-width="23"/>
          <rect x="${pipeX + 5}" y="407" width="24" height="33" rx="2" class="dwfu-preview-steel"/>
          <path d="M ${pipeX - 7} ${pipeTop} H ${pipeX + pipeWidth + 7} L ${pipeX + pipeWidth + 1} ${pipeTop - 16} H ${pipeX - 1} Z" class="dwfu-preview-steel"/>
          <line x1="42" y1="${pipeTop}" x2="42" y2="${pipeBottom}" class="dwfu-preview-measure"/><path d="M 37 ${pipeTop + 8} L 42 ${pipeTop} L 47 ${pipeTop + 8} M 37 ${pipeBottom - 8} L 42 ${pipeBottom} L 47 ${pipeBottom - 8}" class="dwfu-preview-measure"/><text x="34" y="${(pipeTop + pipeBottom) / 2}" class="dwfu-preview-measure-text" transform="rotate(-90 34 ${(pipeTop + pipeBottom) / 2})">${escapeHtml(result.height.customerLabel)}</text>
          <line x1="${pipeX + pipeWidth}" y1="478" x2="${wallX}" y2="478" class="dwfu-preview-distance"/><path d="M ${pipeX + pipeWidth + 5} 474 L ${pipeX + pipeWidth} 478 L ${pipeX + pipeWidth + 5} 482 M ${wallX - 5} 474 L ${wallX} 478 L ${wallX - 5} 482" class="dwfu-preview-distance"/><text x="${(pipeX + pipeWidth + wallX) / 2}" y="497" class="dwfu-preview-distance-text">${result.state.wallDistanceMm} mm</text>
        </svg>
      </div>
      <div class="chimney-configurator__preview-meta dweco-preview-meta">
        <span><small>Höhenklasse</small><strong>${escapeHtml(result.height.customerLabel)}</strong></span>
        <span><small>Technische Höhe</small><strong>${this.millimetres(totalHeight)}</strong></span>
        <span><small>Nennweite</small><strong>DN ${result.state.dn}</strong></span>
        <span><small>Wandabstand</small><strong>${result.state.wallDistanceMm} mm</strong></span>
      </div>`;
    }

    onChange(event) {
      const input = event.target;
      const key = input.dataset.selection;
      if (!key) return;
      this.selection[key] = key === 'heightClassId' ? String(input.value) : Number(input.value);
      this.cartMessage = '';
      this.render();
    }

    async onClick(event) {
      if (event.target.closest('[data-next]')) {
        this.step = this.returnToSummary ? 3 : Math.min(3, this.step + 1);
        this.returnToSummary = false;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-back]')) {
        this.step = Math.max(0, this.step - 1);
        this.render();
        this.focusStep();
        return;
      }
      const edit = event.target.closest('[data-edit-step]');
      if (edit) {
        this.step = Math.max(0, Math.min(2, Number(edit.dataset.editStep)));
        this.returnToSummary = true;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-add-configuration]') && !this.previewOnly) await this.addToCart();
    }

    focusStep() {
      requestAnimationFrame(() => this.querySelector('.chimney-configurator__step:not([hidden]) .chimney-configurator__step-title')?.focus?.());
    }

    bomProperty(result, roles) {
      const accepted = Array.isArray(roles) ? roles : [roles];
      return result.bom
        .filter((line) => accepted.some((role) => role.endsWith('*') ? line.role.startsWith(role.slice(0, -1)) : line.role === role))
        .map((line) => `${line.quantity} × ${line.sku}`)
        .join(' | ');
    }

    kitLineItem(result) {
      return {
        id: String(result.kitVariant.variantId),
        quantity: 1,
        properties: {
          'Technische Höhe': this.millimetres(result.height.technicalMm),
          '_DW-ECO Konfiguration': `${result.state.dn}:${result.state.heightClassId}:${result.state.wallDistanceMm}`,
          '_Wandstütze SKU': this.bomProperty(result, 'WALL_SUPPORT'),
          '_Grundplatte SKU': this.bomProperty(result, 'BASE_PLATE'),
          '_Revision SKU': this.bomProperty(result, 'INSPECTION'),
          '_T-Anschluss SKU': this.bomProperty(result, 'TEE_90'),
          '_DWECO261 SKU': this.bomProperty(result, 'WALL_PASS'),
          '_Längenelemente inkl. Mengen': this.bomProperty(result, 'LENGTH_*'),
          '_Mündungsabschluss SKU': this.bomProperty(result, 'TERMINAL'),
          '_Wandhalter SKU inkl. Menge': this.bomProperty(result, 'WALL_HOLDER'),
          '_Vollständige Komponenten-SKU-Liste': result.bom.map((line) => `${line.quantity} × ${line.sku}`).join(' | ')
        }
      };
    }

    async addToCart() {
      const result = this.resolve();
      if (!result.kitCartEligible || this.submitting) return;
      this.submitting = true;
      this.cartMessage = '';
      this.render();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [this.kitLineItem(result)] })
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.description || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'jeremias-dw-eco-configurator' } }));
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        this.submitting = false;
        this.cartMessage = error.message || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.';
        this.render();
      }
    }
  }

  if (!customElements.get('jeremias-dw-eco-configurator')) {
    customElements.define('jeremias-dw-eco-configurator', JeremiasDwEcoConfigurator);
  }
})();
